import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { getCSRFToken } from "./utils/various";
import {
  Body,
  Company,
  Entity,
  ProvisionalBody,
  ProvisionalContentMedium,
} from "../types";
import ContentMediaList from "./ContentMediaList";
import BodyLink, { ctaOptions } from "./BodyLinks";
import {
  FaceSmileIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  CloudArrowDownIcon,
  PhotoIcon,
  XMarkIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import useChatGPT from "../hook/useChatGpt";
import EmojiPicker from "@emoji-mart/react";
import Modal from "./Modal";
import CustomCloseButton from "./CustomCloseButton";
import ModalInput from "./ModalInput";
import { HiddenInput } from "./DynamicFormGenerator";
import Tooltip from "./Tooltip";
import { GoRepoForked } from "react-icons/go";
import CustomPopover from "./Popover";
import { useTextStyles } from "../hook/useTextStyles";
import { useMentionsAndVariables } from "./utils/publisher/utils";
import VariableSelector from "./Publisher/variables/VariableSelector";
import { eventEmitter } from "../utils/eventEmitter";
import useMultiCloudinaryUpload from "./useMultiCloudinaryUpload";

type Props = {
  setHContentMedia: React.Dispatch<
    React.SetStateAction<Record<number, ProvisionalContentMedium>>
  >;
  allowMediaNeedEditionForm?: boolean;
  allowEdit: boolean;
  index: number;
  hContentMedia: Record<number, ProvisionalContentMedium>;
  isActive: boolean;
  isLast: boolean;
  body: ProvisionalBody;
  setHBodies: React.Dispatch<
    React.SetStateAction<Record<number, ProvisionalBody>>
  >;
  canvaSession: any;
  onRemoval?: (bPid: ProvisionalBody["provisional_id"]) => void;
  onDuplication?: (bPid: ProvisionalBody["provisional_id"]) => void;
  targetsSelectorRenderer: (
    bPid: ProvisionalBody["provisional_id"],
    buttonUI?: JSX.Element
  ) => JSX.Element;
  mentionSelectorRenderer?: (
    body: ProvisionalBody,
    insert: Function
  ) => JSX.Element;
  mentions?: { [k: string]: Entity };
  variables?: Record<string, string | null>;
  aiPostLimit: number;
  aiPostUsageCount: number;
};

export type aiPromptQuotasType = {
  ai_post_usage_count: number;
  ai_post_usage_per_month_limit: number;
};

export const BodyEditor = memo(
  ({
    body,
    canvaSession,
    setHBodies,
    onDuplication,
    onRemoval,
    isActive,
    isLast,
    targetsSelectorRenderer,
    hContentMedia,
    index,
    setHContentMedia,
    mentionSelectorRenderer,
    mentions,
    variables,
    allowMediaNeedEditionForm,
    allowEdit = true,
    aiPostLimit,
    aiPostUsageCount,
  }: Props) => {
    const originalId = useMemo(() => body.id, []);

    const { uploadFiles } = useMultiCloudinaryUpload({
      addMedia: (files) => {
        const lastCMId =
          Math.max(
            ...Object.keys(hContentMedia).map((k) => parseInt(k, 10)),
            0
          ) + 1;
        const newCMs = files.map((file, index) => {
          const newId = lastCMId + index;
          return [
            newId,
            {
              provisional_id: newId,
              format: file.resource_type,
              thumbnail_url: file.url,
              serialized_file: file,
              url: file.url,
            },
          ] as const;
        });
        setHContentMedia((prev) => ({
          ...prev,
          ...Object.fromEntries(newCMs),
        }));
        const contentMediaIds = newCMs.map(([id]) => id);

        afterAddFn(contentMediaIds, body.provisional_id);
      },
    });

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const designId = params.get("design_id");

      if (!designId || !canvaSession?.access_token) return;

      const delay = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms));

      const startExportJob = async (accessToken: string, designId: string) => {
        const response = await fetch(`https://api.canva.com/rest/v1/exports`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            design_id: designId,
            format: { type: "jpg", quality: 100 },
          }),
        });

        if (!response.ok) {
          throw new Error(
            `❌ Échec de la demande d'export: ${response.status}`
          );
        }

        const data = await response.json();
        return data.job.id as string;
      };

      const waitForExportReady = async (
        accessToken: string,
        exportId: string,
        maxAttempts = 12,
        intervalMs = 5000
      ): Promise<string[]> => {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          await delay(intervalMs);

          const response = await fetch(
            `https://api.canva.com/rest/v1/exports/${exportId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `❌ Échec récupération du statut d'export: ${response.status}`
            );
          }

          const data = await response.json();
          const status = data.job.status;

          if (status === "success") {
            return data.job.urls as string[];
          }

          if (status === "failed") {
            throw new Error("❌ L'export Canva a échoué !");
          }
        }

        throw new Error(
          "❌ Timeout: l'export Canva a mis trop de temps à se terminer."
        );
      };

      const downloadImages = async (urls: string[]): Promise<File[]> => {
        const downloads = await Promise.all(
          urls.map(async (url, index) => {
            try {
              const res = await fetch(url, {
                headers: {
                  "Cache-Control": "no-cache, no-store, must-revalidate",
                },
              });

              if (!res.ok) {
                console.error(`❌ Échec téléchargement image ${index + 1}`);
                return null;
              }

              const blob = await res.blob();
              return new File([blob], `design_page_${index + 1}.jpg`, {
                type: "image/jpeg",
              });
            } catch (err) {
              console.error(`❌ Erreur téléchargement image ${index + 1}`, err);
              return null;
            }
          })
        );

        return downloads.filter((file): file is File => file !== null);
      };

      const exportAndUploadDesign = async () => {
        try {
          const exportId = await startExportJob(
            canvaSession.access_token,
            designId
          );
          const exportUrls = await waitForExportReady(
            canvaSession.access_token,
            exportId,
            10,
            1000
          );

          if (exportUrls.length === 0) {
            console.error("❌ Aucune URL d'export récupérée !");
            return;
          }

          const files = await downloadImages(exportUrls);

          if (files.length === 0) {
            console.error("❌ Aucune image valide à uploader !");
            return;
          }

          console.log("📤 Envoi des images à Cloudinary...");
          uploadFiles(files);
        } catch (error) {
          console.error("❌ Erreur lors de l'export Canva :", error);
        }
      };

      exportAndUploadDesign();
    }, [window.location.search, canvaSession?.access_token]);

    useEffect(() => {
      const addOrReplaceMedium = (event: any) => {
        if (event.body_provisional_id != body.provisional_id) {
          return;
        }
        if (event.uuid) {
          // Il y a un uuid, média ajouté en 2 fois (remplacé si existe)
          const { contentMedium, uuid } = event;
          let shallAdd = false;
          setHContentMedia((prev) => {
            if (!prev[uuid]) {
              shallAdd = true;
            }
            return {
              ...prev,
              [uuid]: { provisional_id: uuid, ...contentMedium },
            };
          });
          if (shallAdd) {
            afterAddFn([uuid], body.provisional_id);
          }
        } else {
          // pas d'uuid mais un id, directement ajouté

          const { contentMedium } = event;
          setHContentMedia((prev) => ({
            ...prev,
            [contentMedium.id]: {
              provisional_id: contentMedium.id,
              ...contentMedium,
            },
          }));
          afterAddFn([contentMedium.id], body.provisional_id);
        }
      };

      const addEditToMedia = (event: any) => {
        const { transformedUrl, originalUrl, id } = event;
        const newMedia = Object.entries(hContentMedia).map((e) =>
          e[1]?.url === originalUrl
            ? [e[0], { ...e[1], edit: { transformedUrl, id } }]
            : e
        );
        setHContentMedia(Object.fromEntries(newMedia));
      };

      eventEmitter.on("editSaved", addEditToMedia);
      eventEmitter.on("mediaSelected", addOrReplaceMedium);
      return () => {
        eventEmitter.off("mediaSelected", addOrReplaceMedium);
        eventEmitter.off("editSaved", addEditToMedia);
      };
    }, [hContentMedia, setHContentMedia]);

    // récupéré à la création et ne changera pas
    const originalCmPidByBmId = useMemo(
      () =>
        Object.fromEntries(
          body.body_media_attributes
            .filter((bm) => bm.id)
            .map((bm) => [bm.content_medium_provisional_id, bm.id])
        ),
      []
    ) as Record<number, number>;

    const updateBody = (data: Object) => {
      const pid = body.provisional_id;
      setHBodies((prev) => ({ ...prev, [pid]: { ...prev[pid], ...data } }));
    };

    const duplicateBody = ({ id, ...body }: ProvisionalBody) => {
      let newBodyPid: number | null = null;
      const body_media_attributes = body.body_media_attributes.map(
        ({ id, ...b }) => ({ ...b })
      );
      setHBodies((prev) => {
        newBodyPid =
          Math.max(...Object.keys(prev).map((k) => parseInt(k)), 0) + 1;
        return {
          ...prev,
          [newBodyPid]: {
            ...body,
            message: `${body.message || ""}`,
            provisional_id: newBodyPid,
            body_media_attributes,
          },
        };
      });
      // je reconnais que c'est tordu mais comme ça on a accès à la valeur
      setTimeout(() => {
        if (onDuplication && newBodyPid) {
          onDuplication(newBodyPid);
        }
      }, 0);
    };

    const deleteBody = (body: ProvisionalBody) => {
      onRemoval && onRemoval(body.provisional_id);
      // On remove à l'assignation
      setHBodies(({ [body.provisional_id]: _, ...keep }) => keep);
    };

    const beforeRemoveFn = (
      contentMediaId: ProvisionalContentMedium["provisional_id"],
      bodyId: ProvisionalBody["provisional_id"]
    ) => {
      setHBodies((prev) => ({
        ...prev,
        [bodyId]: {
          ...prev[bodyId],
          body_media_attributes: prev[bodyId]?.body_media_attributes?.filter(
            (cm) => cm.content_medium_provisional_id !== contentMediaId
          ),
        },
      }));
    };

    const afterAddFn = (
      contentMediaIds: ProvisionalContentMedium["provisional_id"][],
      bodyId: ProvisionalBody["provisional_id"]
    ) => {
      setHBodies((prev) => {
        const thisbody = prev[bodyId];
        const lastCMPosition =
          thisbody?.body_media_attributes?.slice(-1)[0]?.position || 0;
        const newBodyContentMedias = contentMediaIds.map((id, index) => ({
          content_medium_provisional_id: id,
          position: lastCMPosition + index + 1,
        }));
        return {
          ...prev,
          [bodyId]: {
            ...thisbody,
            body_media_attributes: [
              ...thisbody.body_media_attributes,
              ...newBodyContentMedias,
            ],
          },
        };
      });
    };

    const afterMoveFn = (
      contentMediaId: number,
      newPosition: number,
      bodyProvisionalId: number
    ) => {
      setHBodies((prev) => {
        const thisBody = prev[bodyProvisionalId];
        const currentPosition = thisBody.body_media_attributes.find(
          (cm) => cm.content_medium_provisional_id === contentMediaId
        )?.position as number;
        // Donne un number > 0 si on attribue une position supérieure à la précdente
        const diffPosition = newPosition - (currentPosition as number);
        // console.log(`diffPosition: ${diffPosition}, currentPosition: ${currentPosition}, newPosition: ${newPosition}`)
        // Répartition des nouvelles positions
        const newBodyContentMedias = thisBody?.body_media_attributes?.map(
          (cm) => {
            // On édite une nouvelle poisition pour le CM modifié
            if (cm.content_medium_provisional_id === contentMediaId) {
              return {
                content_medium_provisional_id: contentMediaId,
                position: newPosition,
              };
            } else {
              // On change la position des autres CM
              // si on augmente la position du CM modifié
              if (diffPosition > 0) {
                // On ne touche pas à ceux qui sont avant la currentPosition et après la newPosition
                if (
                  cm.position < currentPosition &&
                  cm.position > newPosition
                ) {
                  return cm;
                }
                // On va prendre ceux qui sont entre la current (non inclu) et la new position (inclu)
                // Et on va les faire reculer (-1)
                if (
                  cm.position > currentPosition &&
                  cm.position <= newPosition
                ) {
                  return {
                    content_medium_provisional_id:
                      cm.content_medium_provisional_id,
                    position: cm.position - 1,
                  };
                }
              }
              if (diffPosition < 0) {
                // On ne touche pas à ceux qui sont avant la currentPosition et après la newPosition
                if (
                  cm.position > currentPosition &&
                  cm.position < newPosition
                ) {
                  return cm;
                }
                // On va prendre ceux qui sont entre la current (non inclu) et la new position (inclu)
                // Et on va les faire avancer (+1)
                if (
                  cm.position < currentPosition &&
                  cm.position >= newPosition
                ) {
                  return {
                    content_medium_provisional_id:
                      cm.content_medium_provisional_id,
                    position: cm.position + 1,
                  };
                }
              }
              return cm;
            }
          }
        );
        // On met a jour le body avec les nouvelles positions et on les trie
        const sortedCM = newBodyContentMedias.sort(
          (a, b) => a.position - b.position
        );
        return {
          ...prev,
          [bodyProvisionalId]: {
            ...thisBody,
            body_media_attributes: sortedCM,
          },
        };
      });
    };

    const computeFocus = (body: ProvisionalBody) => {
      const media = body.body_media_attributes?.map(
        (bma) => hContentMedia[bma.content_medium_provisional_id]
      );

      const linkUrl = body?.link_url;

      const hasVideo = media.some(
        (m) => !m?.destroy && m?.serialized_file?.resource_type === "video"
      );
      const hasMultipleImages =
        media.filter(
          (m) => !m?.destroy && m?.serialized_file?.resource_type === "image"
        ).length > 1;
      const hasImage = media.some(
        (m) => !m?.destroy && m?.serialized_file?.resource_type === "image"
      );

      const defaultAccept = "image/*,application/pdf,video/*";

      if (linkUrl && !hasVideo) {
        return { focus: "link", accept: "image/*,application/pdf,video/*" };
      } else if (hasVideo) {
        return { focus: "video", accept: "video/*" };
      } else if (hasMultipleImages) {
        return { focus: "images", accept: defaultAccept };
      } else if (hasImage) {
        return { focus: "image", accept: defaultAccept };
      } else {
        return { focus: "text", accept: defaultAccept };
      }
    };
    const { focus, accept } = computeFocus(body);
    const disabled = useMemo(
      () =>
        ["video", "link"].includes(focus) &&
        body.body_media_attributes.length > 0,
      [focus, body]
    );

    useState<File | null>(null);
    // TextArea Buttons
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fakeInputRef = useRef<HTMLDivElement>(null);

    const insert = (inserted: string, inserted_length: number = 0) => {
      inserted_length = inserted_length || inserted.length; // handle mentions (inserted length is smaller than visible length)
      if (inputRef?.current) {
        const { selectionStart, selectionEnd, value } = inputRef.current;
        if (
          typeof selectionStart === "number" &&
          typeof selectionEnd === "number"
        ) {
          // typeOf car 0 est compté comme false
          const newValue = (inputRef.current.value = `${value.slice(
            0,
            selectionStart
          )}${inserted}${value.slice(selectionEnd)}`);

          setHBodies((prev) => ({
            ...prev,
            [body.provisional_id]: {
              ...body,
              message: normalizeMentionsAndVariables(newValue),
            },
          }));
          inputRef?.current?.focus();
          setTimeout(() => {
            // Visiblement nécessaire
            if (inputRef.current) {
              inputRef.current.selectionStart =
                selectionStart + inserted_length;
              inputRef.current.selectionEnd = selectionStart + inserted_length;
            }
          }, 200);
        }
      }
    };

    const [emojiSelectorOpen, setEmojiSelectorOpen] = useState(false);
    const onEmojiSelect = (e: any) => {
      insert(String.fromCodePoint(parseInt(e.unified, 16)));
      setEmojiSelectorOpen(false);
    };
    // AI
    const [prompt, setPrompt] = useState("");
    const [gptSelectionStart, setGptSelectionStart] = useState<number | null>(
      null
    );
    const [textAfterSelection, setTextAfterSelection] = useState<string | null>(
      null
    );
    const [showAiModal, setShowAiModal] = useState<boolean>(false);
    const {
      error: gptError,
      fetchChatGPTResponse,
      isWriting: gptIsWriting,
      response: gptResponse,
      aiPromptQuotas,
    } = useChatGPT({
      apiRoute: "/bodies/openai_call",
      companyContent: window.location.href.includes("/company_contents/new"),
    });

    // AI | Quotas utilisateur
    const [aiQuotas, setAiQuotas] = useState<aiPromptQuotasType>({
      ai_post_usage_count: aiPostUsageCount,
      ai_post_usage_per_month_limit: aiPostLimit,
    });

    // On met à jour les quotas de l'utilisateur pour interroger l'IA
    useEffect(() => {
      if (
        aiPromptQuotas?.ai_post_usage_count &&
        aiPromptQuotas?.ai_post_usage_per_month_limit
      ) {
        setAiQuotas(aiPromptQuotas);
      }
    }, [aiPromptQuotas]);

    // smartlink
    const [showSmartLinkModal, setShowSmartLinkModal] = useState(false);
    const [smartLinkUrl, setSmartLinkUrl] = useState("");
    const [smartLinkLoading, setSmartLinkLoading] = useState(false);
    const [smartLinkError, setSmartLinkError] = useState<string | null>(null);
    const toggleSmartlinkModal = () => {
      setShowSmartLinkModal(!showSmartLinkModal);
      setSmartLinkError(null);
      setSmartLinkUrl("");
    };

    // Si on a une réponse de ChatGPT, on insère le texte dans le message du body
    useEffect(() => {
      if (!gptIsWriting) {
        setGptSelectionStart(null);
      }
      if (gptIsWriting && gptSelectionStart === null) {
        setGptSelectionStart(inputRef?.current?.selectionStart as number);
        setTextAfterSelection(
          body?.message?.slice(inputRef?.current?.selectionStart as number) ||
            ""
        );
      }
      if (gptResponse?.text && gptSelectionStart !== null) {
        if (showAiModal) {
          setShowAiModal(false);
        } // on ferme la modale pour voir le beau texte s'écrire
        const start = body?.message?.slice(0, gptSelectionStart);
        // Mise à jour de l'état (setHBodies) avec le nouveau texte
        setHBodies((prev) => ({
          ...prev,
          [body.provisional_id]: {
            ...body,
            message: start + gptResponse?.text + textAfterSelection,
          },
        }));
      }
    }, [gptResponse, gptIsWriting]);

    // ça a l'air de marcher sans magiquement mais on met quand même :
    const [emojiData, setEmojiData] = useState({});
    useEffect(() => {
      (async () => {
        const response = await fetch(
          "https://cdn.jsdelivr.net/npm/@emoji-mart/data"
        );
        setEmojiData(await response.json());
      })();
    }, []);
    const insertionVariables = useMemo(() => {
      return [
        "$NOM",
        "$ENTREPRISE",
        "$SITE",
        "$MAIL",
        "$TEL",
        "$HASHTAG",
        "$CODE",
        "$LANDING",
        "$NEWSLETTER",
        "$APILINK",
        "$PROMOCODE",
        "$SPONSORLINK",
      ];
    }, []);

    // Links
    const removeLink = () =>
      updateBody({ link_url: "", link_description: "", link_cta: "" });

    // Utils
    const newPidForH = (h: Record<number, Object>) =>
      Math.max(...Object.keys(h).map((k) => parseInt(k)), 0) + 1;

    const replaceBodyWithRemote = async () => {
      setSmartLinkLoading(true);
      const response = await fetch("/bodies/generate_from_url", {
        headers: {
          "X-CSRF-Token": getCSRFToken(),
          "Content-Type": "application/json",
        },
        method: "post",
        body: JSON.stringify({ url: smartLinkUrl }),
      });
      const pid = body.provisional_id;
      if (!response.ok) {
        setSmartLinkError("Nous n'avons pas réussi à récupérer ce contenu");
      } else {
        const resBody: Body = await response.json();
        const body_media_attributes: ProvisionalBody["body_media_attributes"] =
          [];
        resBody.body_media?.forEach((bm) => {
          const newBm = {};
          // medium exists ?
          const existingCm = Object.values(hContentMedia).find(
            (cm) => cm.id === bm.content_medium.id
          );
          const newCmPid = newPidForH(hContentMedia);
          if (!existingCm) {
            // le cm n'est pas encore dans le form (quasi sûr), on le crée
            const cm = { ...bm.content_medium, provisional_id: newCmPid };
            setHContentMedia((prev) => ({
              ...prev,
              [newCmPid.toString()]: cm,
            }));
          }
          body_media_attributes.push({
            id: bm.id,
            position: bm.position || 0,
            content_medium_provisional_id:
              existingCm?.provisional_id || newCmPid,
          });
        });
        const { body_media, ...newBody } = {
          ...resBody,
          body_media_attributes,
          provisional_id: pid,
        };
        setHBodies((prev) => ({ ...prev, [body.provisional_id]: newBody }));
        setTimeout(() => setShowSmartLinkModal(false), 500);
      }
      setSmartLinkLoading(false);
    };

    const onChangeTextArea = (newValue: string) => {
      setHBodies((prev) => ({
        ...prev,
        [body.provisional_id]: { ...body, message: newValue },
      }));
    };

    const { applySelectedStyle } = useTextStyles(
      inputRef,
      body.message || "",
      onChangeTextArea
    );

    const bodyCta = useMemo(
      () => ctaOptions.filter((c) => c.value === body?.link_cta)[0],
      [body?.link_cta]
    );

    const updatePostType = (value: "post" | "post_and_story" | "story") => {
      setHBodies((prevBodies) => ({
        ...prevBodies,
        [body.provisional_id]: {
          ...prevBodies[body.provisional_id],
          post_type: value,
        },
      }));
    };
    // Handling Body for Mentions and Variables
    const {
      normalizeMentionsAndVariables,
      generateMentionOrVariableSpans,
      displayMentionOrVariableNames,
    } = useMentionsAndVariables(mentions, variables || {});

    const [provisionalMessage, setProvisionalMessage] = useState(body.message);

    // On force la mise à jour du textarea
    useEffect(() => {
      setProvisionalMessage(displayMentionOrVariableNames(body.message));
    }, [body.message]);
    const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setProvisionalMessage(displayMentionOrVariableNames(e.target.value));

      setHBodies((prev) => ({
        ...prev,
        [body.provisional_id]: {
          ...body,
          message: normalizeMentionsAndVariables(e.target.value),
        },
      }));
    };

    return (
      <div
        key={`body_provisional_id_${body.provisional_id}_${index}`}
        className={`flex relative z-0 ${
          !isActive && "lg:hidden"
        } flex-col border rounded-b-md rounded-tr-md px-2 py-4 md:px-4 md:py-4 relative bg-white z-0`}
      >
        <div id="header" className="flex justify-between w-full gap-3">
          <div className="flex lg:hidden">
            {targetsSelectorRenderer(body.provisional_id)}
          </div>

          <div className="flex flex-row-reverse flex-wrap items-center justify-between md:justify-between w-full gap-3">
            <div className="flex">
              {/* Options for mobile */}
              <CustomPopover
                position="left"
                disabled={false}
                button={
                  <div className="p-0 btn-neutral lg:hidden h-fit">
                    <EllipsisVerticalIcon className="size-6" />
                  </div>
                }
              >
                <div className="flex flex-col py-2 -mt-4 bg-white rounded-lg shadow-lg w-fit">
                  <button
                    className="flex items-center gap-2 px-4 py-3 whitespace-nowrap hover:bg-gray-50"
                    type="button"
                    onClick={() => duplicateBody(body)}
                  >
                    <GoRepoForked className="size-5" /> Créer une nouvelle
                    version
                  </button>
                  <button
                    type="button"
                    className="flex gap-2 px-4 py-3 text-start hover:bg-gray-50"
                  >
                    {targetsSelectorRenderer(
                      body.provisional_id,
                      <div className="flex gap-2 text-start whitespace-nowrap">
                        <UsersIcon className="size-5 " />
                        Sélection des réseaux sociaux
                      </div>
                    )}
                  </button>
                  {!isLast && (
                    <button
                      className="flex gap-2 px-4 py-3 text-start hover:bg-gray-50"
                      type="button"
                      onClick={() => deleteBody(body)}
                    >
                      <TrashIcon className="size-5 " /> Supprimer cette version
                    </button>
                  )}
                </div>
              </CustomPopover>
              <div className="hidden lg:flex">
                {targetsSelectorRenderer(body.provisional_id)}
              </div>

              <Tooltip
                text="Créer une version différente d'un réseau à l'autre"
                position="bottomleft"
              >
                <button
                  className="hidden gap-2 ml-2 btn-neutral lg:flex"
                  type="button"
                  onClick={() => duplicateBody(body)}
                >
                  <GoRepoForked className="size-5" /> Version
                </button>
              </Tooltip>
              {!isLast && (
                <Tooltip text="Supprimer cette version" position="bottomright">
                  <button
                    className="hidden gap-2 ml-2 btn-neutral lg:flex"
                    type="button"
                    onClick={() => deleteBody(body)}
                  >
                    <TrashIcon className="size-5" /> Supprimer
                  </button>
                </Tooltip>
              )}
            </div>

            {/* Toogle for isStory input */}
            <div className="stonly-post-format flex gap-1 h-fit rounded-full bg-gray-950/5 p-1">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`bodies[${index}][post_type]`}
                  value="post"
                  checked={body.post_type === "post"}
                  onChange={() => updatePostType("post")}
                  className="peer hidden"
                />
                <span className="group flex items-center rounded-full px-2 py-0.5 text-gray-500 peer-checked:text-gray-700 text-xs font-medium peer-checked:bg-white peer-checked:ring-1 peer-checked:ring-gray-950/5">
                  Post
                </span>
              </label>

              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`bodies[${index}][post_type]`}
                  value="story"
                  checked={body.post_type === "story"}
                  onChange={() => updatePostType("story")}
                  className="peer hidden"
                />
                <span className="group flex items-center rounded-full px-2 py-0.5 text-gray-500 peer-checked:text-gray-700 text-xs font-medium peer-checked:bg-white peer-checked:ring-1 peer-checked:ring-gray-950/5">
                  Story
                </span>
              </label>

              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name={`bodies[${index}][post_type]`}
                  value="post_and_story"
                  checked={body.post_type === "post_and_story"}
                  onChange={() => updatePostType("post_and_story")}
                  className="peer hidden"
                />
                <span className="group flex items-center rounded-full px-2 py-0.5 text-gray-500 peer-checked:text-gray-700 text-xs font-medium peer-checked:bg-white peer-checked:ring-1 peer-checked:ring-gray-950/5">
                  Double
                </span>
              </label>
            </div>

            <div className="flex items-start gap-1 flex-grow md:flex-grow-0">
              <button
                type="button"
                onClick={() => applySelectedStyle("bold")}
                className="flex items-center justify-center w-6 text-lg font-bold border rounded-md cursor-pointer hover:bg-gray-50"
              >
                B
              </button>
              <button
                type="button"
                onClick={() => applySelectedStyle("italic")}
                className="flex items-center justify-center w-6 text-lg italic border rounded-md cursor-pointer hover:bg-gray-50"
              >
                I
              </button>
            </div>
          </div>
        </div>
        <div className="relative flex flex-col w-full my-2">
          <div
            className="caret-black w-full max-w-full min-w-full max-h-full min-h-full h-full absolute inset-0 rounded-md whitespace-pre-wrap overflow-y-auto break-words no-scrollbar"
            // On reproduit le style de la div du textarea
            ref={fakeInputRef}
            style={{
              zIndex: -1,
              fontSize: "1rem",
              lineHeight: "1.5rem",
              padding: ".5rem .75rem",
            }}
          >
            <div
              className="min-w-full min-h-full"
              style={{
                ...(inputRef?.current?.scrollHeight && {
                  height: inputRef?.current?.scrollHeight,
                }),
                ...(inputRef?.current?.scrollWidth && {
                  width: inputRef?.current?.scrollWidth,
                }),
                width: inputRef?.current?.scrollHeight,
              }}
            >
              {generateMentionOrVariableSpans(
                normalizeMentionsAndVariables(provisionalMessage || "")
              )}
            </div>
          </div>

          <textarea
            ref={inputRef}
            placeholder="Saisissez votre texte ici"
            className="caret-black text-transparent bg-transparent w-full h-full placeholder-gray-400 border-0 focus:ring-0 whitespace-pre-wrap break-words"
            //required
            rows={10}
            value={provisionalMessage || ""}
            onChange={handleInput}
            onScroll={(e) => {
              fakeInputRef.current?.scrollTo(
                e.target.scrollLeft,
                e.target.scrollTop
              );
            }}
          />
        </div>
        {/* hidden inputs */}
        <textarea
          name={`bodies[${index}][message]`}
          className="hidden"
          value={body.message ?? ""}
        />
        <input
          type="hidden"
          name={`bodies[${index}][id]`}
          value={body.id || ""}
        />
        <input
          type="hidden"
          name={`bodies[${index}][provisional_id]`}
          value={body.provisional_id}
        />
        <BodyMediaInputs
          prefix={`bodies[${index}][body_media_attributes]`}
          originalCmPidByBmId={
            body.id === originalId ? originalCmPidByBmId : {}
          }
          bodyMedia={body.body_media_attributes}
        />
        <div className="flex flex-wrap mb-4">
          {/* Content medias - Uploader + Drag&Drop + Displaying Medias */}
          <ContentMediaList
            allowMediaNeedEditionForm={allowMediaNeedEditionForm}
            allowEdit={allowEdit}
            accept={accept}
            disabled={disabled}
            hContentMedia={hContentMedia}
            setHContentMedia={setHContentMedia}
            selectedContentMedias={body.body_media_attributes?.map(
              (bma) => bma?.content_medium_provisional_id
            )}
            beforeRemoveFn={(contentMediaId: number) =>
              beforeRemoveFn(contentMediaId, body.provisional_id)
            }
            afterMoveFn={(contentMediaId: number, newPosition: number) =>
              afterMoveFn(contentMediaId, newPosition, body.provisional_id)
            }
          />
        </div>
        {/* Links Displaying */}
        {body?.link_url && (
          <div className="relative flex flex-col gap-2 px-3 py-4 mt-1 mb-4 bg-gray-100  group max-w-80 rounded-b-md">
            <button
              type="button"
              className={`flex md:hidden group-hover:flex absolute -top-2 -right-2 justify-center items-center border w-6 h-6 border-red-400 text-xs text-red-400 bg-white rounded-full cursor-pointer !hover:bg-red-400 !hover:text-white hover:bg-red-400`}
              onClick={removeLink}
            >
              <XMarkIcon className="w-4 h-4 text-red-500 hover:text-white" />
            </button>
            <p className="font-bold text-gray-600 truncate">
              {body?.link_url &&
                (() => {
                  try {
                    return new URL(body?.link_url).hostname;
                  } catch {
                    return "";
                  }
                })()}
            </p>
            <p className="text-sm text-gray-800 truncate">
              {body?.link_description || ""}
            </p>
            {bodyCta?.value && body?.link_cta && (
              <span className="absolute bottom-2 right-2 btn-neutral !px-2 !py-1 flex text-sm">
                {bodyCta.label}
              </span>
            )}
          </div>
        )}
        {/* Editing Features */}
        <div className="relative flex flex-wrap justify-between gap-5 items-end">
          <div className="relative flex items-center justify-start gap-2 sm:gap-3">
            {/* Body Link button */}
            <BodyLink
              body={body}
              updateBody={updateBody}
              disabled={["images", "videos"].includes(focus)}
            />
            <input
              type="hidden"
              name={`bodies[${index}][link_url]`}
              value={body?.link_url ?? ""}
            />
            <input
              type="hidden"
              name={`bodies[${index}][link_description]`}
              value={body?.link_description ?? ""}
            />
            <input
              type="hidden"
              name={`bodies[${index}][link_cta]`}
              value={body?.link_cta ?? ""}
            />

            {/* Button Content medias */}
            <Tooltip text="Insérer images ou vidéo" position="bottom">
              <a
                type="button"
                href={`/content_media?mode=media_selector&selection_args[body_provisional_id]=${body.provisional_id}&origin=media_picker_from_post_form_modal`}
                data-turbo-frame="library_media_picker_from_post_form_modal"
                className="btn-small-round"
                onClick={() =>
                  !disabled &&
                  window.openDialog("media_picker_from_post_form_modal")
                }
              >
                <PhotoIcon
                  className={`size-7 ${
                    disabled
                      ? "text-gray-400 hover:text-gray-400"
                      : "hover:text-brand_darker"
                  }`}
                />
              </a>
            </Tooltip>

            <Tooltip text="Insérer des émojis dans le texte" position="bottom">
              <button
                type="button"
                onClick={() => setEmojiSelectorOpen(true)}
                className="btn-small-round"
              >
                <FaceSmileIcon className="size-7" />
              </button>
            </Tooltip>

            <div
              className={`${
                emojiSelectorOpen ? "" : "hidden"
              } absolute -bottom-12 origin-bottom border rounded !z-50 h-96 max-h-96 min-h-96 overflow-auto`}
            >
              <EmojiPicker
                perLine={9}
                maxFrequentRows={1}
                previewPosition="bottom"
                navPosition="top"
                searchPosition="top"
                theme="light"
                onClickOutside={() => {
                  emojiSelectorOpen ? setEmojiSelectorOpen(false) : null;
                }}
                onEmojiSelect={onEmojiSelect}
              />
            </div>
            <div className="relative">
              <Tooltip
                text="Génération du contenu grâce à l'intelligence artificielle"
                position="bottom"
              >
                <button
                  type="button"
                  className="relative btn-small-round"
                  onClick={() => {
                    setShowAiModal(!showAiModal);
                  }}
                >
                  IA
                </button>
              </Tooltip>
              <Modal open={showAiModal} setOpen={setShowAiModal}>
                <div className="relative p-12 my-auto bg-white rounded-md">
                  <CustomCloseButton
                    open={showAiModal}
                    setOpen={setShowAiModal}
                  />
                  <ModalInput
                    title="Générateur de contenu par IA"
                    aiPostLimit={aiQuotas.ai_post_usage_per_month_limit}
                    aiPostUsageCount={aiQuotas.ai_post_usage_count}
                    value={prompt}
                    message="Entrez le sujet de votre article, donnez des détails sur le contenu que vous souhaitez créer."
                    placeholder="Exemple: Rédigez un article sur les tendances immo 2025"
                    setValue={setPrompt}
                    loading={gptIsWriting}
                    error={gptError}
                    Submit={() => fetchChatGPTResponse(prompt)}
                  />
                </div>
              </Modal>
            </div>
            <div className="relative">
              <VariableSelector variables={variables} insert={insert} />
            </div>
            <div>
              <Tooltip
                text="Construire un contenu à partir d'une URL"
                position="bottomright"
              >
                <button
                  type="button"
                  className="btn-small-round"
                  onClick={toggleSmartlinkModal}
                >
                  <CloudArrowDownIcon className="size-6" />
                </button>
              </Tooltip>
              <Modal open={showSmartLinkModal} setOpen={setShowSmartLinkModal}>
                <div className="relative p-12 my-auto bg-white rounded-md">
                  <CustomCloseButton
                    open={showSmartLinkModal}
                    setOpen={setShowSmartLinkModal}
                  />
                  <ModalInput
                    title="Smartlink"
                    value={smartLinkUrl}
                    aiPostLimit={aiQuotas.ai_post_usage_per_month_limit}
                    aiPostUsageCount={aiQuotas.ai_post_usage_count}
                    message="Entrez l'url d'un article et nous écrirons pour vous une accroche, cela remplacera le contenu actuel."
                    placeholder="https://example.com/article"
                    setValue={setSmartLinkUrl}
                    loading={smartLinkLoading}
                    error={smartLinkError}
                    Submit={replaceBodyWithRemote}
                  />
                </div>
              </Modal>
            </div>
            {/* Le rendu du selecteur de mention */}
            {mentionSelectorRenderer?.(body, insert)}
          </div>
        </div>
      </div>
    );
  }
);

const BodyMediaInputs = memo(
  ({
    originalCmPidByBmId,
    bodyMedia,
    prefix,
  }: {
    originalCmPidByBmId: Record<number, number>;
    bodyMedia: ProvisionalBody["body_media_attributes"];
    prefix: string;
  }) => {
    // originalCmPidByBmId permet de réinstaurer les mêmes ids si même content_media
    // mais cas des smartlinks ou l'id existe mais n'était pas présent au début
    // donc on check originalCmPidByBmId[cmPid] || id
    const contentMediaPids = bodyMedia.map(
      (bma) => bma.content_medium_provisional_id
    );
    const destroyables = Object.entries(originalCmPidByBmId).filter(
      ([bm, _]) => !contentMediaPids.includes(parseInt(bm))
    );
    const destIndex = bodyMedia.length + 1;

    return (
      <>
        {bodyMedia.map(
          ({ content_medium_provisional_id: cmPid, position, id }, i) => (
            <div key={cmPid}>
              <HiddenInput
                name={`${prefix}[${i}][id]`}
                value={originalCmPidByBmId[cmPid] || id}
              />
              <HiddenInput
                name={`${prefix}[${i}][content_medium_provisional_id]`}
                value={cmPid}
              />
              <HiddenInput
                name={`${prefix}[${i}][position]`}
                value={position}
              />
            </div>
          )
        )}
        {destroyables.map(([c, pid], i) => (
          <div key={c}>
            <HiddenInput name={`${prefix}[${i + destIndex}][id]`} value={pid} />
            <HiddenInput
              name={`${prefix}[${i + destIndex}][_destroy]`}
              value={1}
            />
          </div>
        ))}
      </>
    );
  }
);

export default BodyEditor;
